import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TrackerService } from '@app/shared/services/tracker.service';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { TranslateService } from '@ngx-translate/core';
import { NEVER } from 'rxjs';
import { switchMap } from 'rxjs/operators';
/*
  This component is a custom Sina-like dialog.
  The needs for this dialog were so different from the normal Sina flow
  that it was necessary to create this as a separate component rather
  than try to hack Sina to fit the need (which was attempted at first).
*/
@Component({
  selector: 'dgx-tag-rating-checkup-tip',
  templateUrl: './tag-rating-checkup-tip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagRatingCheckupTipComponent
  extends SubscriberBaseDirective
  implements OnInit, OnChanges
{
  @Input() public tags: TagsApi.Tag[];

  /** Close the Chekcup Tip card */
  @Output() public dismiss = new EventEmitter();

  public i18n: { [key: string]: string } = {};
  private modalAutoLoaded = false;

  constructor(
    private translateService: TranslateService,
    private tagRatingService: TagRatingService,
    private trackerService: TrackerService,
    private webEnvironmentService: WebEnvironmentService,
    private tagsService: TagsService,
    private route: ActivatedRoute
  ) {
    super();
  }

  public get degreedLogo(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/branding/degreed-logo-glasses.svg',
      true
    );
  }

  public ngOnInit(): void {
    this.i18n = this.translateService.instant([
      'dgSkillsCheckup_message',
      'dgSkillsCheckup_UpdateRatings',
      'dgSkillsCheckup_DoneRating',
    ]);

    this.trackerService.trackEventData({
      action: 'SINA Message Viewed',
      properties: {
        NotificationType: 'UpdateUserTagRatingsOnProfile',
      },
    });
  }

  public ngOnChanges({ tags }: SimpleChanges): void {
    // Delay modal display until tags are loaded
    if (this.hasCheckup() && !this.modalAutoLoaded && tags.currentValue) {
      this.modalAutoLoaded = true;
      this.updateRatings();
    }
  }

  public updateRatings() {
    this.tagRatingService
      .openMultiRatingModal('Skills Checkup', this.tags)
      .pipe(
        switchMap(
          (reason) =>
            reason === 'done'
              ? this.tagsService.completeSkillsCheckup(false)
              : NEVER // don't complete skills checkup if modal is dismissed
        )
      )
      .pipe(this.takeUntilDestroyed()) // NEVER has to be cleaned up manually
      .subscribe(() => {
        this.dismiss.emit();
        this.trackEventData(
          'CallbackAndClose',
          this.i18n.dgSkillsCheckup_UpdateRatings,
          true
        );
      });
  }

  public doneRating() {
    this.tagsService.completeSkillsCheckup().subscribe(() => {
      this.dismiss.emit();
      this.trackEventData(
        'MarkReadAndClose',
        this.i18n.dgSkillsCheckup_DoneRating,
        false
      );
    });
  }

  /**
   * Check to see if Sina notification was sent via server-side (/me?p=1&checkup=true)
   * which prompts the user to update their ratings. It shows up on the Profile → Skills tab
   * {@link https://degreedjira.atlassian.net/wiki/spaces/PROD/pages/5079695964/Skill+Checkup+Tip+SINA+Message#Adding-the-SINA-Message-(77---UpdateUserTagRatingsOnProfile) Skill Checkup Tip SINA Message}.
   */
  private hasCheckup(): boolean {
    // Parse query string
    const value =
      this.route.snapshot?.queryParams.checkup === 'true' ? true : false;
    return value;
  }

  private trackEventData(
    ActionCall: string,
    Text: string,
    Preferred: boolean
  ): void {
    this.trackerService.trackEventData({
      action: 'SINA Message Clicked',
      properties: {
        NotificationType: 'UpdateUserTagRatingsOnProfile',
        Preferred,
        Text,
        ActionCall,
      },
    });
  }
}
